import { call, delay, put, select, spawn } from "@redux-saga/core/effects";
import axios from "axios";

import config from "@/messenger/config";
import { RequestURIs } from "@/messenger/constants/requests";
import { contactsSelectors } from "@/messenger/ducks/entities/contacts";
import { topicsSelectors } from "@/messenger/ducks/entities/topics";
import { setRefinementResultAC } from "@/messenger/ducks/general/conversationRefinementsReducer.duck";
import { getRequestTrackerById } from "@/messenger/ducks/selectors/requestTrackers";
import { chatsSelectors } from "@/messenger/ducks/userInterface/chats";
import { formatContactAddress, formatContactName } from "@/messenger/models/contact";
import { conversationRefinementsSelector, conversationsSelector } from "@/messenger/selectors";
import { ConversationCategory, TConversationNormalized } from "@/messenger/types/entities/conversation";
import { ChannelTypeEnum } from "@/messenger/types/graphql/autogenerated";
import { TStore } from "@/messenger/types/store.types";
import { getChatCategoryCounts } from "@/messenger/utils/getChatCategoryOptions";
import { convertToNumbers } from "@/messenger/utils/helpers";

import { onSelectorDataChange } from "./util";
import { userSelector } from "@/messenger/ducks/selectors/general";
import { TUser } from "@/messenger/types/auth.types";

function* handleRefinementChange() {
    const { search: { value: searchQuery }, filter: { selectedTopicName: campaignQuery, selectedMonth: monthQuery, isSmsCapable: smsQuery, selectedNumber: selectedNumber, selectedRepresentative } } = yield select(conversationRefinementsSelector);
    const conversations = yield select(conversationsSelector);

    const state = yield select((s: TStore) => s);
    
    const searchQueryFilter = (c: TConversationNormalized) => {
        if (!searchQuery) {
            return true;
        }
        
        const contact = contactsSelectors.selectById(state, c.contact);

        const businessName = contact?.businessName ? contact?.businessName : "";

        const address = contact?.address ? formatContactAddress(contact.address) : "";

        const phoneNumber = contact?.phoneNumber
            ? convertToNumbers(`${contact?.phoneNumber?.number}${contact?.phoneNumber?.extension ?? ""}`)
            : "";
        const phoneNumberMatch = phoneNumber?.includes(convertToNumbers(searchQuery));

        const searchableString = `${formatContactName(contact)} ${businessName} ${address}`.toLowerCase();
        const genericMatch = searchableString?.includes(searchQuery.toLowerCase());
        return phoneNumberMatch || genericMatch;
    };

    const campaignQueryFilter = (c: TConversationNormalized) => {
        if (!campaignQuery) {
            return true;
        }

        return c.topicIds.find(topicId => {
            const topic = topicsSelectors.selectById(state, topicId);

            return topic?.name === campaignQuery;
        });
    };

    const monthQueryFilter = (c: TConversationNormalized) => {
        if (!monthQuery) {
            return true;
        }

        return c.participationMonths?.includes(monthQuery);
    };

    const smsCapableFilter = (c: TConversationNormalized) => {
        if (!smsQuery) {
            return true;
        }

        const contact = contactsSelectors.selectById(state, c.contact);

        return contact?.capabilities?.includes(ChannelTypeEnum.Sms) || contact?.capabilities?.includes(ChannelTypeEnum.SmsAndVoice);
    };

    const outboundNumberFilter = (c: TConversationNormalized) => {
        if (!selectedNumber) return true;

        return c.representativePhone?.includes(selectedNumber);
    }
    
    const representativeFilter = (c: TConversationNormalized) => {
        if (!selectedRepresentative) return true;

        return c.representativeId?.includes(selectedRepresentative);
    }

    const removeArchivedConvos = (c: TConversationNormalized) => {
        if(c.category !== ConversationCategory.ARCHIVED) return c;
    }

    const filteredConversations = conversations
        .filter(removeArchivedConvos)
        .filter(searchQueryFilter)
        .filter(campaignQueryFilter)
        .filter(smsCapableFilter)
        .filter(monthQueryFilter)
        .filter(outboundNumberFilter)
        .filter(representativeFilter);

    yield put(setRefinementResultAC(filteredConversations));
}

let countState = null;
function* reassignment() {
    const request = yield select(getRequestTrackerById(RequestURIs.GET_CONVERSATIONS));

    if (request.status.complete) {
        const { result } = yield select(conversationRefinementsSelector);
        const user: TUser = yield select(userSelector);
        const counts = getChatCategoryCounts(result, user?.identity);

        if (countState === null || countState !== JSON.stringify(counts)) {
            countState = JSON.stringify(counts);
            const { token, sessionId } = window.Touchless;
            if (token && sessionId) {
                yield axios.post(
                    config.api.touchless,
                    {
                        query: `mutation updateSession ($sessionId: uuid!, $counts:jsonb)  {
                            UpdateSession(
                                pk_columns: {id: $sessionId},
                                _set: {counts: $counts}
                            ) {
                                id
                            }
                        }
                        `,
                        variables: {
                            sessionId,
                            counts,
                        },
                    },
                    {
                        headers: {
                            "Content-Type": "application/json",
                            "Authorization": `Bearer ${token}`,
                            "TOKEN": token,
                        },
                    },
                );
            }
            if (!token) {
                console.error("Missing touchless token");
            }
            if (!sessionId) {
                console.error("Missing touchless sessionId");
            }
        }
    }
    console.groupEnd();
}

let waiting = false;
/**
 * Throttles and debounces the refinementChange saga to occur once every 500ms
 */
function* refinementLoadBalancer() {
    if (!waiting) {
        waiting = true;
        yield call(handleRefinementChange);
        yield delay(500);
        waiting = false;
        yield call(handleRefinementChange);

        // Touchless Reassignment logic
        yield call(reassignment);
    }
}

export default function* refinementSaga() {
    yield spawn(onSelectorDataChange, conversationRefinementsSelector, refinementLoadBalancer);
    
    yield spawn(onSelectorDataChange, conversationsSelector, refinementLoadBalancer);
    yield spawn(onSelectorDataChange, contactsSelectors.selectAll, refinementLoadBalancer);
    yield spawn(onSelectorDataChange, topicsSelectors.selectAll, refinementLoadBalancer);
    yield spawn(onSelectorDataChange, chatsSelectors.selectAll, refinementLoadBalancer);
}
